import { db, Project, Photo, Sal, FloorPlan, FloorPlanPoint, TypologyPrice, StandaloneMap } from '../db/database';
import { apiFetch, isHomeserverConfigured } from '../lib/homeserver';
import { apiStorageFrom } from '../lib/storageShim';
import { convertRemoteToLocalMapping, convertRemoteToLocalProject } from './conflictResolution';
import { convertRemoteToLocalStructure } from '../db/structures';
import { getPendingEntityIds } from '../db/onlineFirst';
import { pruneProjectLocal } from '../db/projects';

const REMOTE_IN_BATCH_SIZE = 150;
// Numero massimo di righe restituite per singola chiamata API homeserver.
// Il server impone limit=100 di default; sovrascriviamo esplicitamente per evitare
// troncamenti silenziosi quando un batch contiene molti ID.
const SYNC_FETCH_LIMIT = 1000;
async function downloadStorageBlobFromPublicUrl(publicUrl: string): Promise<Blob | null> {
  const parsedUrl = new URL(publicUrl);
  // Supporto per URL storage legacy Supabase-era (/storage/v1/object/public|sign/<bucket>/<path>)
  // e per URL shim proxy (/api/storage/<bucket>/<path>)
  const match =
    parsedUrl.pathname.match(/\/storage\/v1\/object\/(?:public|sign)\/([^/]+)\/(.+)/) ??
    parsedUrl.pathname.match(/\/api\/storage\/(?!proxy\/|sign-one|remove|upload-presigned)([^/]+)\/(.+)/);

  if (!match) {
    return null;
  }

  const [, bucketName, rawObjectPath] = match;
  const objectPath = decodeURIComponent(rawObjectPath);
  const { data: blob, error } = await apiStorageFrom(bucketName).download(objectPath);

  if (error || !blob) {
    console.warn(`⚠️  Failed to download storage object ${objectPath}: ${error?.message}`);
    return null;
  }

  return blob;
}

async function downloadStoragePdfBase64(publicUrl: string): Promise<string | undefined> {
  const pdfBlob = await downloadStorageBlobFromPublicUrl(publicUrl);
  if (!pdfBlob) {
    return undefined;
  }

  const arrayBuffer = await pdfBlob.arrayBuffer();
  const bytes = new Uint8Array(arrayBuffer);
  const CHUNK = 0x8000;
  let binary = '';
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + CHUNK)));
  }

  return btoa(binary);
}

function normalizeStandaloneMapGridConfig(gridConfig: any): StandaloneMap['gridConfig'] {
  return {
    rows: typeof gridConfig?.rows === 'number' ? gridConfig.rows : 10,
    cols: typeof gridConfig?.cols === 'number' ? gridConfig.cols : 10,
    offsetX: typeof gridConfig?.offsetX === 'number' ? gridConfig.offsetX : 0,
    offsetY: typeof gridConfig?.offsetY === 'number' ? gridConfig.offsetY : 0,
  };
}

function normalizeStandaloneMapPoints(points: any): StandaloneMap['points'] {
  if (!Array.isArray(points)) {
    return [];
  }

  return points.map((point: any) => ({
    id: point.id,
    pointType: point.pointType,
    pointX: point.pointX,
    pointY: point.pointY,
    labelX: point.labelX,
    labelY: point.labelY,
    perimeterPoints: point.perimeterPoints,
    customText: point.customText,
    labelText: point.labelText,
    labelBackgroundColor: point.labelBackgroundColor,
    labelTextColor: point.labelTextColor,
    eiRating: point.eiRating ?? undefined,
  }));
}

// ============================================
// SEZIONE: Download Progetti (Project Download)
// Scarica tutti i progetti accessibili dall'utente (o tutti se admin).
// Preserva la preferenza locale syncEnabled e risolve i conflitti.
// ============================================

function ensureOnline(): void {
  if (!isHomeserverConfigured()) {
    throw new Error('Homeserver not configured');
  }
  if (!navigator.onLine) {
    throw new Error('No internet connection');
  }
}

function chunkArray<T>(items: T[], chunkSize: number): T[][] {
  const chunks: T[][] = [];
  for (let index = 0; index < items.length; index += chunkSize) {
    chunks.push(items.slice(index, index + chunkSize));
  }
  return chunks;
}

async function fetchRowsByIds(
  table: string,
  column: string,
  ids: string[]
): Promise<any[]> {
  const rows: any[] = [];
  const batches = chunkArray(ids, REMOTE_IN_BATCH_SIZE);

  for (const batch of batches) {
    // homeserver parser: ?col=in.val1,val2 (comma-separated, no parentheses)
    const idsParam = batch.join(',');
    let offset = 0;
    while (true) {
      const res = await apiFetch(`/api/${table}?${column}=in.${idsParam}&limit=${SYNC_FETCH_LIMIT}&offset=${offset}`);
      if (!res.ok) {
        throw new Error(`Failed to download ${table}: ${res.statusText}`);
      }
      const { data } = await res.json() as { data: any[] };
      const page: any[] = data || [];
      rows.push(...page);
      // Se la pagina è piena, potrebbero esserci altre righe — continua con offset.
      if (page.length < SYNC_FETCH_LIMIT) break;
      offset += SYNC_FETCH_LIMIT;
    }
  }

  return rows;
}

async function fetchRowsPaged(path: string): Promise<any[]> {
  const rows: any[] = [];
  const separator = path.includes('?') ? '&' : '?';
  let offset = 0;

  while (true) {
    const res = await apiFetch(`${path}${separator}limit=${SYNC_FETCH_LIMIT}&offset=${offset}`);
    if (!res.ok) {
      throw new Error(`Failed to download ${path}: ${res.statusText}`);
    }
    const { data } = await res.json() as { data: any[] };
    const page = data || [];
    rows.push(...page);
    if (page.length < SYNC_FETCH_LIMIT) break;
    offset += SYNC_FETCH_LIMIT;
  }

  return rows;
}

async function getAccessibleProjectsFromRemote(_userId: string, _isAdmin: boolean): Promise<any[]> {
  // Scope enforced server-side — no client-side filter needed
  return fetchRowsPaged('/api/projects');
}

async function getAccessibleLocalProjects(userId: string, isAdmin: boolean): Promise<Project[]> {
  if (isAdmin) {
    return db.projects.toArray();
  }

  return db.projects
    .where('ownerId')
    .equals(userId)
    .or('accessibleUsers')
    .equals(userId)
    .toArray();
}

async function getSyncIncludeArchivedProjects(): Promise<boolean> {
  const includeArchivedMeta = await db.metadata.get('syncIncludeArchivedProjects');
  return includeArchivedMeta?.value === true;
}

async function getSyncEligibleProjects(userId: string, isAdmin: boolean): Promise<Project[]> {
  const includeArchivedProjects = await getSyncIncludeArchivedProjects();
  const projects = await getAccessibleLocalProjects(userId, isAdmin);
  const syncEnabledProjects = projects.filter((project) => project.syncEnabled === 1);

  return includeArchivedProjects
    ? syncEnabledProjects
    : syncEnabledProjects.filter((project) => project.archived !== 1);
}

function extractStorageLocation(
  url: string | undefined
): { bucket: string; path: string } | null {
  if (!url) {
    return null;
  }

  const fullMatch = url.match(/\/storage\/v1\/object\/(?:sign|public)\/([^/]+)\/([^?]+)/);
  if (fullMatch) {
    return {
      bucket: decodeURIComponent(fullMatch[1]),
      path: decodeURIComponent(fullMatch[2]),
    };
  }

  const legacyMatch = url.match(/\/(planimetrie|floor-plans|photos)\/([^?]+)/);
  if (legacyMatch) {
    return {
      bucket: decodeURIComponent(legacyMatch[1]),
      path: decodeURIComponent(legacyMatch[2]),
    };
  }

  return null;
}

async function fetchStorageBlob(
  storagePath: string | undefined,
  fallbackUrl?: string | undefined
): Promise<Blob | undefined> {
  if (storagePath) {
    const { data, error } = await apiStorageFrom('photos').download(storagePath);
    if (error) {
      throw error;
    }
    return data ?? undefined;
  }

  if (fallbackUrl) {
    const response = await fetch(fallbackUrl);
    if (!response.ok) {
      throw new Error(`Failed to fetch asset: ${response.status}`);
    }
    return response.blob();
  }

  return undefined;
}

async function fetchBucketBlob(
  location: { bucket: string; path: string } | null,
  fallbackUrl?: string | undefined
): Promise<Blob | undefined> {
  if (location) {
    const { data, error } = await apiStorageFrom(location.bucket).download(location.path);
    if (error) {
      throw error;
    }
    return data ?? undefined;
  }

  if (fallbackUrl) {
    const response = await fetch(fallbackUrl);
    if (!response.ok) {
      throw new Error(`Failed to fetch asset: ${response.status}`);
    }
    return response.blob();
  }

  return undefined;
}

export async function downloadProjectsFromSupabase(userId: string, isAdmin = false): Promise<number> {
  ensureOnline();

  const remoteProjects = await getAccessibleProjectsFromRemote(userId, isAdmin);
  const pendingIds = await getPendingEntityIds('project');

  let downloadedCount = 0;

  for (const remoteProject of remoteProjects) {
    const converted = convertRemoteToLocalProject(remoteProject);
    const existingProject = await db.projects.get(converted.id);
    const projectToStore: Project = {
      ...converted,
      syncEnabled: existingProject?.syncEnabled ?? 1,
    };

    if (pendingIds.has(projectToStore.id)) {
      continue;
    }

    await db.projects.put(projectToStore);
    downloadedCount += 1;
  }

  // Pruning: rimuovere localmente i progetti non più accessibili remoto
  const remoteProjectIds = new Set(remoteProjects.map((p) => p.id));
  const localProjects = await db.projects.toArray();
  for (const local of localProjects) {
    if (!remoteProjectIds.has(local.id) && !pendingIds.has(local.id)) {
      await pruneProjectLocal(local.id);
    }
  }

  return downloadedCount;
}

export async function downloadMappingEntriesFromSupabase(userId: string, isAdmin = false): Promise<number> {
  ensureOnline();

  const projects = await getSyncEligibleProjects(userId, isAdmin);
  const projectIds = projects.map((project) => project.id);
  if (projectIds.length === 0) {
    return 0;
  }

  const data = await fetchRowsByIds('mapping_entries', 'project_id', projectIds);

  const pendingIds = await getPendingEntityIds('mapping_entry');
  let downloadedCount = 0;

  // F5: pre-carico le righe locali una sola volta per (a) saltare le scritture
  // di righe immutate e (b) riusarle nel pruning, evitando una seconda query.
  const existingLocal = await db.mappingEntries.where('projectId').anyOf(projectIds).toArray();
  const existingById = new Map(existingLocal.map((e) => [e.id, e]));

  for (const remoteEntry of data || []) {
    if (pendingIds.has(remoteEntry.id)) {
      continue;
    }

    const entry = convertRemoteToLocalMapping(remoteEntry);
    const existing = existingById.get(entry.id);
    // F5: salta il put se la riga locale è già identica (stesso lastModified),
    // riducendo il churn IndexedDB e i re-render inutili a ogni ciclo di sync.
    if (existing && entry.lastModified != null && existing.lastModified === entry.lastModified) {
      continue;
    }
    await db.mappingEntries.put(entry);
    downloadedCount += 1;
  }

  // Pruning: rimuovere localmente le mapping entries non più presenti remoto
  const remoteEntryIds = new Set((data || []).map((r: any) => r.id));
  const localEntryIds = existingLocal.map((e) => e.id);
  const toDeleteEntries = localEntryIds.filter((id) => !remoteEntryIds.has(id) && !pendingIds.has(id));
  if (toDeleteEntries.length > 0) await db.mappingEntries.bulkDelete(toDeleteEntries);

  return downloadedCount;
}

export async function downloadPhotosFromSupabase(
  userId: string,
  isAdmin = false,
  options?: { includeBlobs?: boolean; includeThumbnailBlobs?: boolean }
): Promise<{ downloaded: number; failed: number }> {
  ensureOnline();

  const projects = await getSyncEligibleProjects(userId, isAdmin);
  const projectIds = projects.map((project) => project.id);
  if (projectIds.length === 0) {
    return { downloaded: 0, failed: 0 };
  }

  const mappingEntries = await db.mappingEntries.where('projectId').anyOf(projectIds).toArray();
  const mappingEntryIds = mappingEntries.map((entry) => entry.id);
  const structureEntries = await db.structureEntries.where('projectId').anyOf(projectIds).toArray();
  const structureEntryIds = structureEntries.map((entry) => entry.id);
  if (mappingEntryIds.length === 0 && structureEntryIds.length === 0) {
    return { downloaded: 0, failed: 0 };
  }

  const pendingIds = await getPendingEntityIds('photo');
  const expectedPhotoIds = new Set<string>();
  const photoRows: any[] = [];

  const mappingEntryIdBatches = chunkArray(mappingEntryIds, REMOTE_IN_BATCH_SIZE);
  for (const batch of mappingEntryIdBatches) {
    const idsParam = batch.join(',');
    const data = await fetchRowsPaged(`/api/photos?mapping_entry_id=in.${idsParam}`);
    for (const row of data) {
      expectedPhotoIds.add(row.id);
    }
    photoRows.push(...data);
  }

  const structureEntryIdBatches = chunkArray(structureEntryIds, REMOTE_IN_BATCH_SIZE);
  for (const batch of structureEntryIdBatches) {
    const idsParam = batch.join(',');
    const data = await fetchRowsPaged(`/api/photos?structure_entry_id=in.${idsParam}`);
    for (const row of data) {
      expectedPhotoIds.add(row.id);
    }
    photoRows.push(...data);
  }

  const localPhotoScopeEntryIds = [...mappingEntryIds, ...structureEntryIds];
  const localPhotos = localPhotoScopeEntryIds.length > 0
    ? await db.photos.where('mappingEntryId').anyOf(localPhotoScopeEntryIds).toArray()
    : [];
  const locallyUnsyncedPhotoIds = new Set(
    localPhotos.filter((photo) => !photo.uploaded).map((photo) => photo.id)
  );
  const staleLocalPhotoIds = localPhotos
    .map((photo) => photo.id)
    .filter((photoId) =>
      !expectedPhotoIds.has(photoId)
      && !pendingIds.has(photoId)
      && !locallyUnsyncedPhotoIds.has(photoId)
    );

  if (staleLocalPhotoIds.length > 0) {
    await db.photos.bulkDelete(staleLocalPhotoIds);
  }

  const shouldFetchAllRows = options?.includeBlobs === true;

  if (!shouldFetchAllRows) {
    const fetchedById = new Map(photoRows.map((row) => [row.id, row]));
    const photoIdBatches = chunkArray(Array.from(expectedPhotoIds), REMOTE_IN_BATCH_SIZE);
    for (const batch of photoIdBatches) {
      const unresolvedIds = batch.filter((id) => !fetchedById.has(id));
      if (unresolvedIds.length === 0) {
        continue;
      }

      const idsParam = unresolvedIds.join(',');
      const data = await fetchRowsPaged(`/api/photos?id=in.${idsParam}`);
      for (const row of data) {
        fetchedById.set(row.id, row);
      }
    }

    photoRows.length = 0;
    for (const photoId of Array.from(expectedPhotoIds)) {
      const row = fetchedById.get(photoId);
      if (row) {
        photoRows.push(row);
      }
    }
  } else {
    const photoRowById = new Map(photoRows.map((row) => [row.id, row]));
    photoRows.length = 0;
    photoRows.push(...Array.from(photoRowById.values()));
  }

  let downloaded = 0;
  let failed = 0;

  for (const remotePhoto of photoRows) {
    try {
      if (pendingIds.has(remotePhoto.id)) {
        continue;
      }

      const existingPhoto = await db.photos.get(remotePhoto.id);
      let blob = existingPhoto?.blob;
      let thumbnailBlob = existingPhoto?.thumbnailBlob;

      if (options?.includeBlobs && !blob) {
        blob = await fetchStorageBlob(remotePhoto.storage_path || undefined, remotePhoto.url || undefined);
      }

      // F4: la thumbnail si scarica anche in modalità "solo thumbnail" (offline
      // leggero). Il blob full sopra resta condizionato a includeBlobs.
      if ((options?.includeBlobs || options?.includeThumbnailBlobs) && !thumbnailBlob) {
        thumbnailBlob = await fetchStorageBlob(
          remotePhoto.thumbnail_storage_path || undefined,
          remotePhoto.thumbnail_url || undefined
        );
      }

      const photo: Photo = {
        id: remotePhoto.id,
        mappingEntryId: remotePhoto.mapping_entry_id || remotePhoto.structure_entry_id,
        entryType: remotePhoto.structure_entry_id ? 'structure' : 'mapping',
        blob,
        thumbnailBlob,
        metadata: remotePhoto.metadata,
        uploaded: true,
        remoteUrl: remotePhoto.url || undefined,
        thumbnailRemoteUrl: remotePhoto.thumbnail_url || undefined,
        storagePath: remotePhoto.storage_path || undefined,
        thumbnailStoragePath: remotePhoto.thumbnail_storage_path || undefined,
      };

      await db.photos.put(photo);
      downloaded += 1;
    } catch (error) {
      console.warn(`Failed to hydrate photo ${remotePhoto.id}`, error);
      failed += 1;
    }
  }

  // Pruning: rimuovere localmente le foto non più presenti remoto
  const remotePhotoIds = new Set(photoRows.map((r: any) => r.id));
  const localPhotoIds = localPhotoScopeEntryIds.length > 0
    ? await db.photos.where('mappingEntryId').anyOf(localPhotoScopeEntryIds).primaryKeys() as string[]
    : [];
  const toDeletePhotos = localPhotoIds.filter((id) =>
    !remotePhotoIds.has(id)
    && !pendingIds.has(id)
    && !locallyUnsyncedPhotoIds.has(id)
  );
  if (toDeletePhotos.length > 0) await db.photos.bulkDelete(toDeletePhotos);

  return { downloaded, failed };
}

export async function downloadFloorPlansFromSupabase(
  userId: string,
  isAdmin = false,
  options?: { includeImageBlobs?: boolean; includeThumbnailBlobs?: boolean; includePdf?: boolean }
): Promise<number> {
  ensureOnline();

  const projects = await getSyncEligibleProjects(userId, isAdmin);
  const projectIds = projects.map((project) => project.id);
  if (projectIds.length === 0) {
    return 0;
  }

  const data = await fetchRowsByIds('floor_plans', 'project_id', projectIds);

  const pendingIds = await getPendingEntityIds('floor_plan');
  let downloadedCount = 0;

  for (const remoteFloorPlan of data || []) {
    if (pendingIds.has(remoteFloorPlan.id)) {
      continue;
    }

    const existingFloorPlan = await db.floorPlans.get(remoteFloorPlan.id);
    const imageLocation = extractStorageLocation(remoteFloorPlan.image_url || undefined);
    const thumbnailLocation = extractStorageLocation(remoteFloorPlan.thumbnail_url || undefined);
    const pdfLocation = extractStorageLocation(remoteFloorPlan.pdf_url || undefined);

    // Se l'immagine remota è cambiata (es. sostituzione planimetria da Gestione Dati),
    // l'URL contiene un nuovo timestamp: invalidiamo il blob cache locale così il
    // progetto mostra la nuova planimetria invece di quella vecchia ancora in cache.
    const imageChanged = !!existingFloorPlan && existingFloorPlan.imageUrl !== (remoteFloorPlan.image_url || undefined);
    const thumbnailChanged = !!existingFloorPlan && existingFloorPlan.thumbnailUrl !== (remoteFloorPlan.thumbnail_url || undefined);
    const pdfChanged = !!existingFloorPlan && existingFloorPlan.pdfUrl !== (remoteFloorPlan.pdf_url || undefined);

    let imageBlob = imageChanged ? undefined : existingFloorPlan?.imageBlob;
    let thumbnailBlob = thumbnailChanged ? undefined : existingFloorPlan?.thumbnailBlob;
    let pdfBlobBase64 = pdfChanged ? undefined : existingFloorPlan?.pdfBlobBase64;

    if (options?.includeImageBlobs && !imageBlob) {
      imageBlob = await fetchBucketBlob(imageLocation, remoteFloorPlan.image_url || undefined);
    }

    if (options?.includeThumbnailBlobs && !thumbnailBlob) {
      thumbnailBlob = await fetchBucketBlob(thumbnailLocation, remoteFloorPlan.thumbnail_url || undefined);
    }

    if (options?.includePdf && !pdfBlobBase64) {
        const pdfBlob = await fetchBucketBlob(pdfLocation, remoteFloorPlan.pdf_url || undefined);
        if (pdfBlob) {
          const arrayBuffer = await pdfBlob.arrayBuffer();
          const bytes = new Uint8Array(arrayBuffer);
          let binary = '';
          for (let index = 0; index < bytes.length; index += 1) {
            binary += String.fromCharCode(bytes[index]);
          }
          pdfBlobBase64 = btoa(binary);
        }
      }

    const floorPlan: FloorPlan = {
      id: remoteFloorPlan.id,
      projectId: remoteFloorPlan.project_id,
      floor: remoteFloorPlan.floor,
      imageBlob,
      thumbnailBlob,
      imageUrl: remoteFloorPlan.image_url || undefined,
      thumbnailUrl: remoteFloorPlan.thumbnail_url || undefined,
      pdfBlobBase64,
      pdfUrl: remoteFloorPlan.pdf_url || undefined,
      originalFilename: remoteFloorPlan.original_filename,
      originalFormat: remoteFloorPlan.original_format,
      width: remoteFloorPlan.width,
      height: remoteFloorPlan.height,
      gridEnabled: existingFloorPlan?.gridEnabled,
      gridConfig: existingFloorPlan?.gridConfig,
      metadata: remoteFloorPlan.metadata || {},
      createdBy: remoteFloorPlan.created_by,
      createdAt: new Date(remoteFloorPlan.created_at).getTime(),
      updatedAt: new Date(remoteFloorPlan.updated_at).getTime(),
      remoteUpdatedAt: new Date(remoteFloorPlan.updated_at).getTime(),
      synced: 1,
    };

    await db.floorPlans.put(floorPlan);
    downloadedCount += 1;
  }

  // Pruning: rimuovere localmente i floor plan non più presenti remoto
  const remoteFloorPlanIds = new Set((data || []).map((r: any) => r.id));
  const localFloorPlanIds = projectIds.length > 0
    ? await db.floorPlans.where('projectId').anyOf(projectIds).primaryKeys() as string[]
    : [];
  const toDeleteFP = localFloorPlanIds.filter((id) => !remoteFloorPlanIds.has(id) && !pendingIds.has(id));
  if (toDeleteFP.length > 0) await db.floorPlans.bulkDelete(toDeleteFP);

  return downloadedCount;
}

export async function downloadFloorPlanPointsFromSupabase(userId: string, isAdmin = false): Promise<number> {
  ensureOnline();

  const floorPlans = await db.floorPlans.toArray();
  const localProjects = await getSyncEligibleProjects(userId, isAdmin);
  const accessibleProjectIds = new Set(localProjects.map((project) => project.id));
  const floorPlanIds = floorPlans
    .filter((floorPlan) => accessibleProjectIds.has(floorPlan.projectId))
    .map((floorPlan) => floorPlan.id);

  if (floorPlanIds.length === 0) {
    return 0;
  }

  const data = await fetchRowsByIds('floor_plan_points', 'floor_plan_id', floorPlanIds);

  const pendingIds = await getPendingEntityIds('floor_plan_point');
  let downloadedCount = 0;

  for (const remotePoint of data || []) {
    if (pendingIds.has(remotePoint.id)) {
      continue;
    }

    const existingPoint = await db.floorPlanPoints.get(remotePoint.id);
    const point: FloorPlanPoint = {
      id: remotePoint.id,
      floorPlanId: remotePoint.floor_plan_id,
      // mappingEntryId: fallback su structure_entry_id per compatibilità display (StructureWizard usa questo campo per filtrare/navigare)
      mappingEntryId: remotePoint.mapping_entry_id || remotePoint.structure_entry_id || undefined,
      structureEntryId: remotePoint.structure_entry_id || undefined,
      pointType: remotePoint.point_type,
      pointX: remotePoint.point_x,
      pointY: remotePoint.point_y,
      labelX: remotePoint.label_x,
      labelY: remotePoint.label_y,
      perimeterPoints: remotePoint.perimeter_points,
      customText: remotePoint.custom_text,
      eiRating: existingPoint?.eiRating ?? remotePoint.ei_rating ?? remotePoint.metadata?.eiRating,
      metadata: remotePoint.metadata || {},
      createdBy: remotePoint.created_by,
      createdAt: new Date(remotePoint.created_at).getTime(),
      updatedAt: new Date(remotePoint.updated_at).getTime(),
      remoteUpdatedAt: new Date(remotePoint.updated_at).getTime(),
      synced: 1,
    };

    await db.floorPlanPoints.put(point);
    downloadedCount += 1;
  }

  // Pruning: rimuovere localmente i points non più presenti remoto.
  // Protegge anche item con retryCount >= 5 (permanentFailed): sono ancora in queue (synced=0)
  // ma getPendingEntityIds li esclude perché retryCount >= 5.
  const remotePointIds = new Set((data || []).map((r: any) => r.id));
  const localPointIds = floorPlanIds.length > 0
    ? await db.floorPlanPoints.where('floorPlanId').anyOf(floorPlanIds).primaryKeys() as string[]
    : [];
  const queuedEntityIds = new Set(
    (await db.syncQueue.where('entityType').equals('floor_plan_point').and(i => i.synced === 0).toArray())
      .map(i => i.entityId)
  );
  const toDeletePoints = localPointIds.filter((id) => !remotePointIds.has(id) && !queuedEntityIds.has(id));
  if (toDeletePoints.length > 0) await db.floorPlanPoints.bulkDelete(toDeletePoints);

  return downloadedCount;
}
// ============================================
// SEZIONE: Download Mappe Standalone (Standalone Maps Download)
// Scarica le mappe standalone dell'utente e idrata lazy il PDF originale.
// ============================================

export async function downloadStandaloneMapsFromSupabase(userId: string, isAdmin: boolean = false): Promise<number> {
  if (!isHomeserverConfigured()) {
    console.warn('⚠️  Download skipped: Homeserver not configured');
    return 0;
  }

  if (!navigator.onLine) {
    console.warn('⚠️  Download skipped: No internet connection');
    return 0;
  }

  console.log(`⬇️  Download mappe standalone dal server per utente ${userId}${isAdmin ? ' (admin)' : ''}...`);

  try {
    // Scope enforced server-side: gli utenti non admin vedono solo le proprie mappe.
    const standaloneMaps = await fetchRowsPaged('/api/standalone_maps');

    if (!standaloneMaps || standaloneMaps.length === 0) {
      console.log('✅ No standalone maps to download');
      return 0;
    }

    let downloadedCount = 0;

    for (const remoteMap of standaloneMaps) {
      try {
        const existingMap = await db.standaloneMaps.get(remoteMap.id);
        const remoteUpdated = new Date(remoteMap.updated_at).getTime();
        const shouldKeepLocalData = !!existingMap && remoteUpdated <= existingMap.updatedAt;
        const shouldHydrateMissingPdf = !!remoteMap.pdf_url && !existingMap?.pdfBlobBase64;
        const shouldHydrateMissingImage = !!remoteMap.image_url && !existingMap?.imageBlob;
        const shouldHydrateMissingThumbnail = !!remoteMap.thumbnail_url && !existingMap?.thumbnailBlob;

        if (
          existingMap &&
          shouldKeepLocalData &&
          existingMap.imageBlob &&
          !shouldHydrateMissingPdf &&
          !shouldHydrateMissingImage &&
          !shouldHydrateMissingThumbnail
        ) {
          console.log(`⏭️  Standalone map ${remoteMap.id} is up to date, skipping`);
          continue;
        }

        let imageBlob = existingMap?.imageBlob;
        if (remoteMap.image_url && (!imageBlob || remoteUpdated > (existingMap?.updatedAt || 0))) {
          try {
            imageBlob = (await downloadStorageBlobFromPublicUrl(remoteMap.image_url)) || imageBlob;
          } catch (imageErr) {
            console.warn(`⚠️  Failed to download standalone image for ${remoteMap.id}:`, imageErr);
          }
        }

        let thumbnailBlob = existingMap?.thumbnailBlob;
        if (remoteMap.thumbnail_url && (!thumbnailBlob || remoteUpdated > (existingMap?.updatedAt || 0))) {
          try {
            thumbnailBlob = (await downloadStorageBlobFromPublicUrl(remoteMap.thumbnail_url)) || thumbnailBlob;
          } catch (thumbnailErr) {
            console.warn(`⚠️  Failed to download standalone thumbnail for ${remoteMap.id}:`, thumbnailErr);
          }
        }

        let pdfBlobBase64 = existingMap?.pdfBlobBase64;
        if (remoteMap.pdf_url && (!pdfBlobBase64 || remoteUpdated > (existingMap?.updatedAt || 0))) {
          try {
            pdfBlobBase64 = (await downloadStoragePdfBase64(remoteMap.pdf_url)) || pdfBlobBase64;
            if (pdfBlobBase64) {
              console.log(`📄 Downloaded PDF originale for standalone map ${remoteMap.id}`);
            }
          } catch (pdfErr) {
            console.warn(`⚠️  Failed to download standalone PDF for ${remoteMap.id}:`, pdfErr);
          }
        }

        const resolvedImageBlob = imageBlob || existingMap?.imageBlob;
        if (!resolvedImageBlob) {
          console.warn(`⚠️  Skipping standalone map ${remoteMap.id}: image blob unavailable`);
          continue;
        }

        const baseMap = shouldKeepLocalData ? existingMap : undefined;
        const remotePoints = normalizeStandaloneMapPoints(remoteMap.points);
        const remoteGridConfig = normalizeStandaloneMapGridConfig(remoteMap.grid_config);

        const standaloneMap: StandaloneMap = {
          id: remoteMap.id,
          userId: baseMap?.userId ?? remoteMap.user_id,
          name: baseMap?.name ?? remoteMap.name,
          description: baseMap?.description ?? remoteMap.description ?? undefined,
          imageBlob: resolvedImageBlob,
          thumbnailBlob: thumbnailBlob || baseMap?.thumbnailBlob,
          pdfBlobBase64,
          imageUrl: baseMap?.imageUrl ?? remoteMap.image_url ?? undefined,
          thumbnailUrl: baseMap?.thumbnailUrl ?? remoteMap.thumbnail_url ?? undefined,
          pdfUrl: baseMap?.pdfUrl ?? remoteMap.pdf_url ?? undefined,
          originalFilename: baseMap?.originalFilename ?? remoteMap.original_filename,
          originalFormat: baseMap?.originalFormat ?? remoteMap.original_format ?? undefined,
          width: baseMap?.width ?? remoteMap.width,
          height: baseMap?.height ?? remoteMap.height,
          points: baseMap?.points ?? remotePoints,
          gridEnabled: baseMap?.gridEnabled ?? !!remoteMap.grid_enabled,
          gridConfig: baseMap?.gridConfig ?? remoteGridConfig,
          metadata: baseMap?.metadata ?? remoteMap.metadata ?? {},
          createdAt: baseMap?.createdAt ?? new Date(remoteMap.created_at).getTime(),
          updatedAt: baseMap?.updatedAt ?? remoteUpdated,
          synced: baseMap?.synced ?? (1 as 0 | 1),
        };

        await db.standaloneMaps.put(standaloneMap);
        downloadedCount++;
        console.log(`✅ Downloaded standalone map: ${remoteMap.id}`);
      } catch (mapErr) {
        const errorMessage = mapErr instanceof Error ? mapErr.message : String(mapErr);
        console.error(`❌ Error downloading standalone map ${remoteMap.id}:`, errorMessage);
      }
    }

    console.log(`✅ Scaricate ${downloadedCount} mappe standalone dal server`);
    return downloadedCount;
  } catch (err) {
    const errorMessage = err instanceof Error ? err.message : String(err);
    console.error('❌ Failed to download standalone maps:', errorMessage);
    throw err;
  }
}

// ============================================
// SEZIONE: Aggiornamento flag foto remote (Remote Photo Flags)
// Confronta il conteggio foto sul server con quello locale.
// Imposta hasRemotePhotos=true sulle entries che hanno foto non scaricate.
// Chiamata quando l'utente sceglie di non scaricare le foto durante il sync manuale.
// ============================================

export async function updateRemotePhotosFlags(userId: string, isAdmin: boolean): Promise<void> {
  const projects = await getSyncEligibleProjects(userId, isAdmin);
  const projectIds = projects.map((project) => project.id);
  if (projectIds.length === 0) {
    return;
  }

  const entries = await db.mappingEntries.where('projectId').anyOf(projectIds).toArray();
  const entryIds = entries.map((entry) => entry.id);
  if (entryIds.length === 0) {
    return;
  }

  const photos = await db.photos.where('mappingEntryId').anyOf(entryIds).toArray();
  const remoteOnlyMap = new Map<string, boolean>();

  for (const photo of photos) {
    if (!photo.blob && (photo.remoteUrl || photo.storagePath)) {
      remoteOnlyMap.set(photo.mappingEntryId, true);
    }
  }

  for (const entry of entries) {
    await db.mappingEntries.update(entry.id, {
      hasRemotePhotos: remoteOnlyMap.get(entry.id) ? true : false,
    });
  }
}

export async function downloadSalsFromSupabase(userId: string, isAdmin = false): Promise<number> {
  ensureOnline();

  const projects = await getSyncEligibleProjects(userId, isAdmin);
  const projectIds = projects.map((project) => project.id);
  if (projectIds.length === 0) {
    return 0;
  }

  const data = await fetchRowsByIds('sals', 'project_id', projectIds);

  const pendingIds = await getPendingEntityIds('sal');
  let downloadedCount = 0;

  for (const remoteSal of data || []) {
    if (pendingIds.has(remoteSal.id)) {
      continue;
    }

    const sal: Sal = {
      id: remoteSal.id,
      projectId: remoteSal.project_id,
      number: remoteSal.number,
      name: remoteSal.name || undefined,
      date: remoteSal.date,
      notes: remoteSal.notes || undefined,
      createdAt: new Date(remoteSal.created_at).getTime(),
      synced: 1,
    };

    await db.sals.put(sal);
    downloadedCount += 1;
  }

  // Pruning: rimuovere localmente i SAL non più presenti remoto
  const remoteSalIds = new Set((data || []).map((r: any) => r.id));
  const localSalIds = projectIds.length > 0
    ? await db.sals.where('projectId').anyOf(projectIds).primaryKeys() as string[]
    : [];
  const toDeleteSals = localSalIds.filter((id) => !remoteSalIds.has(id) && !pendingIds.has(id));
  if (toDeleteSals.length > 0) await db.sals.bulkDelete(toDeleteSals);

  return downloadedCount;
}

export async function downloadTypologyPricesFromSupabase(userId: string, isAdmin = false): Promise<number> {
  ensureOnline();

  const projects = await getSyncEligibleProjects(userId, isAdmin);
  const projectIds = projects.map((project) => project.id);
  if (projectIds.length === 0) {
    return 0;
  }

  const data = await fetchRowsByIds('typology_prices', 'project_id', projectIds);

  const pendingIds = await getPendingEntityIds('typology_price');
  let downloadedCount = 0;

  for (const remotePrice of data || []) {
    if (pendingIds.has(remotePrice.id)) {
      continue;
    }

    const price: TypologyPrice = {
      id: remotePrice.id,
      projectId: remotePrice.project_id,
      attraversamento: remotePrice.attraversamento,
      tipologicoId: remotePrice.tipologico_id || undefined,
      pricePerUnit: remotePrice.price_per_unit,
      unit: remotePrice.unit,
      createdAt: remotePrice.created_at ? new Date(remotePrice.created_at).getTime() : undefined,
      updatedAt: remotePrice.updated_at ? new Date(remotePrice.updated_at).getTime() : undefined,
      synced: 1,
    };

    await db.typologyPrices.put(price);
    downloadedCount += 1;
  }

  // Pruning: rimuovere localmente i prezzi non più presenti remoto
  const remotePriceIds = new Set((data || []).map((r: any) => r.id));
  const localPriceIds = projectIds.length > 0
    ? await db.typologyPrices.where('projectId').anyOf(projectIds).primaryKeys() as string[]
    : [];
  const toDeletePrices = localPriceIds.filter((id) => !remotePriceIds.has(id) && !pendingIds.has(id));
  if (toDeletePrices.length > 0) await db.typologyPrices.bulkDelete(toDeletePrices);

  return downloadedCount;
}

export async function downloadStructureEntriesFromSupabase(userId: string, isAdmin = false): Promise<number> {
  ensureOnline();

  const projects = await getSyncEligibleProjects(userId, isAdmin);
  const projectIds = projects.map((project) => project.id);
  if (projectIds.length === 0) {
    return 0;
  }

  const data = await fetchRowsByIds('structure_entries', 'project_id', projectIds);

  const pendingIds = await getPendingEntityIds('structure_entry');
  let downloadedCount = 0;

  // F5: pre-carico le righe locali una sola volta (skip scritture immutate + pruning).
  const existingLocal = await db.structureEntries.where('projectId').anyOf(projectIds).toArray();
  const existingById = new Map(existingLocal.map((e) => [e.id, e]));

  for (const remoteEntry of data || []) {
    if (pendingIds.has(remoteEntry.id)) {
      continue;
    }

    const entry = convertRemoteToLocalStructure(remoteEntry);
    const existing = existingById.get(entry.id);
    // F5: salta il put se la riga locale è già identica (stesso lastModified).
    if (existing && entry.lastModified != null && existing.lastModified === entry.lastModified) {
      continue;
    }
    await db.structureEntries.put(entry);
    downloadedCount += 1;
  }

  // Pruning: rimuovere localmente le structure entries non più presenti remoto
  const remoteEntryIds = new Set((data || []).map((r: any) => r.id));
  const localEntryIds = existingLocal.map((e) => e.id);
  const toDeleteEntries = localEntryIds.filter((id) => !remoteEntryIds.has(id) && !pendingIds.has(id));
  if (toDeleteEntries.length > 0) await db.structureEntries.bulkDelete(toDeleteEntries);

  return downloadedCount;
}
